// src/utils/excelParser.js
import * as XLSX from 'xlsx';

/**
 * Parse Excel file and return array of row objects
 * @param {File} file - Excel file to parse
 * @returns {Promise<Object>} - Parsed data with rows and headers
 */
export async function parseExcelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          raw: false, // Convert to strings
          defval: '', // Default value for empty cells
        });
        
        // Get headers
        const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] || [];
        
        resolve({
          rows: jsonData,
          headers: headers,
          rowCount: jsonData.length,
          sheetName: firstSheetName
        });
      } catch (error) {
        reject(new Error(`Error al leer el archivo Excel: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Generate Excel file from data array
 * @param {Array} data - Array of objects to convert to Excel
 * @param {string} filename - Output filename
 * @returns {Blob} - Excel file blob
 */
export function generateExcelFile(data, filename = 'entregas.xlsx') {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Entregas');
  
  // Generate buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  return new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
}

/**
 * Download Excel template with comprehensive instructions and district list
 * Merged from existing DownloadExcelTemplate.jsx
 */
export function downloadExcelTemplate() {
  // Main template data (without Fecha column - date selected in UI)
  const data = [
    ["Nombre y Apellido", "WhatsApp", "Distrito", "SKU", "Cantidad", "Producto", "Cobrar", "Notas", "Direccion"],
    ["John Doe", "912345678", "Miraflores", "FAJA-001", "2", "Faja Clásica", "89.90", "Ejemplo de nota", "Av. Ejemplo 123"],
    ["Maria Garcia", "987654321", "San Isidro", "CINT-002", "1", "", "45.00", "Llamar antes", "Jr. Los Pinos 456"]
  ];

  // Detailed instructions
  const instructions = [
    ["INSTRUCCIONES IMPORTANTES - FAST MAX CARGA MASIVA"],
    [""],
    ["✨ NOVEDAD: La fecha se selecciona en la interfaz web, NO en este Excel"],
    [""],
    ["📋 CAMPOS OBLIGATORIOS:"],
    ["• Nombre y Apellido: Nombre completo del destinatario"],
    ["• WhatsApp: Exactamente 9 dígitos (sin código de país +51)"],
    ["• Distrito: Debe coincidir exactamente con la lista de distritos"],
    ["• Direccion: Dirección completa de entrega"],
    [""],
    ["🏷️ CAMPOS DE PRODUCTO (al menos uno es obligatorio):"],
    ["• SKU: Código del producto (RECOMENDADO - más preciso)"],
    ["• Producto: Descripción del producto (opcional si hay SKU)"],
    [""],
    ["📦 CAMPOS OPCIONALES:"],
    ["• Cantidad: Número de unidades (por defecto: 1)"],
    ["• Cobrar: Monto a cobrar contraentrega (por defecto: 0)"],
    ["• Notas: Información adicional para el courier"],
    [""],
    ["⚠️ NOTAS IMPORTANTES:"],
    ["• Si proporciona SKU, el sistema usará el precio registrado del producto"],
    ["• Si no proporciona SKU, debe llenar el campo 'Producto' con descripción"],
    ["• La columna 'Cantidad' solo aplica cuando se proporciona un SKU"],
    ["• Verifique que el SKU exista en el inventario antes de subir"],
    ["• El sistema validará y sugerirá correcciones antes de guardar"],
    [""],
    ["📍 DISTRITOS DISPONIBLES:"],
    ["Copie exactamente el nombre del distrito de la lista en la pestaña 'Distritos'"],
    ["El sistema detectará errores comunes y sugerirá el distrito correcto"]
  ];

  // Complete district list from backend driver_assignments
  const districts = [
    "Ancon", "Ate", "Barranco", "Breña", "Carabayllo", "Chorrillos",
    "Chosica", "Comas", "El Agustino", "Huaycan", "Independencia", "Jesús María",
    "La Molina", "La Victoria", "Lince", "Los Olivos", "Lurigancho", "Lurin",
    "Magdalena del Mar", "Miraflores", "Pachacamac", "Pueblo Libre", "Puente Piedra",
    "Rimac", "San Borja", "San Isidro", "San Juan de Lurigancho", "San Juan de Miraflores",
    "San Luis", "San Martin de Porres", "San Miguel", "Santa Anita", "Santa Rosa",
    "Santiago de Surco", "Surquillo", "Villa El Salvador", "Villa Maria del Triunfo",
    "Cercado de Lima", "Callao", "Bellavista", "Carmen de la Legua", "La Perla",
    "La Punta", "Ventanilla"
  ];

  const districtSheetData = [
    ["LISTA DE DISTRITOS VÁLIDOS - FAST MAX"],
    [""],
    ["⚠️ Copie el nombre EXACTAMENTE como aparece aquí:"],
    [""],
    ["🗺️ ZONA SUR:"],
    ["Chorrillos", "San Juan de Miraflores", "Villa El Salvador", "Villa Maria del Triunfo", "Barranco", "Surquillo", "Lurin", "Pachacamac"],
    [""],
    ["🗺️ ZONA CENTRO:"],
    ["San Isidro", "San Borja", "Miraflores", "Santiago de Surco", "Lince", "Jesús María", "Magdalena del Mar", "San Miguel", "Pueblo Libre", "Breña", "Cercado de Lima", "La Victoria"],
    [""],
    ["🗺️ ZONA ESTE:"],
    ["Ate", "Santa Anita", "El Agustino", "Lurigancho", "La Molina", "San Juan de Lurigancho", "San Luis", "Chosica", "Huaycan"],
    [""],
    ["🗺️ ZONA NORTE:"],
    ["Comas", "Independencia", "San Martin de Porres", "Los Olivos", "Rimac", "Puente Piedra", "Carabayllo", "Ancon", "Santa Rosa"],
    [""],
    ["🗺️ CALLAO:"],
    ["Callao", "Bellavista", "Carmen de la Legua", "La Perla", "La Punta", "Ventanilla"],
    [""],
    ["💡 CONSEJO: El sistema detectará errores comunes como:"],
    ["• 'milaflores' → sugerirá 'Miraflores'"],
    ["• 'san isidro' → sugerirá 'San Isidro'"],
    ["• 'sjl' → sugerirá 'San Juan de Lurigancho'"]
  ];

  // SKU examples
  const skuExamples = [
    ["EJEMPLOS DE SKU Y PRODUCTOS"],
    [""],
    ["SKU", "Producto", "Precio", "Descripción"],
    ["FAJA-001", "Faja Clásica Negra", "89.90", "Faja reductora de cintura"],
    ["FAJA-002", "Faja Premium Beige", "129.90", "Faja con compresión alta"],
    ["CINT-001", "Cinturón Deportivo M", "45.00", "Cinturón para ejercicio"],
    ["CINT-002", "Cinturón Deportivo L", "45.00", "Cinturón para ejercicio talla L"],
    [""],
    ["💡 CONSEJOS:"],
    ["• Contacte a su administrador para obtener la lista completa de SKUs"],
    ["• Verifique que el SKU esté activo en el inventario"],
    ["• Use SKU en lugar de 'Producto' para mayor precisión"],
    ["• Si usa SKU, el campo 'Producto' puede dejarse vacío"],
    ["• El sistema mostrará sus productos disponibles al editar entregas"]
  ];

  // Convert data to sheets
  const wsData = XLSX.utils.aoa_to_sheet(data);
  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
  const wsDistricts = XLSX.utils.aoa_to_sheet(districtSheetData);
  const wsSKU = XLSX.utils.aoa_to_sheet(skuExamples);

  // Set column widths for better readability
  wsData['!cols'] = [
    { wch: 20 }, // Nombre
    { wch: 12 }, // WhatsApp
    { wch: 20 }, // Distrito
    { wch: 12 }, // SKU
    { wch: 10 }, // Cantidad
    { wch: 25 }, // Producto
    { wch: 10 }, // Cobrar
    { wch: 30 }, // Notas
    { wch: 35 }  // Direccion
  ];

  wsInstructions['!cols'] = [{ wch: 90 }];
  wsDistricts['!cols'] = [{ wch: 80 }];
  wsSKU['!cols'] = [{ wch: 15 }, { wch: 25 }, { wch: 10 }, { wch: 35 }];

  // Create workbook and append sheets
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsData, "Plantilla");
  XLSX.utils.book_append_sheet(wb, wsInstructions, "Instrucciones");
  XLSX.utils.book_append_sheet(wb, wsDistricts, "Distritos");
  XLSX.utils.book_append_sheet(wb, wsSKU, "Ejemplos SKU");

  // Write file
  XLSX.writeFile(wb, "Plantilla_Carga_Masiva_FastMax.xlsx");
}